"use client"

import { cn } from "src/utils/Functions"
import { motion } from "motion/react"

function AnimatedCheckCircle({ className }: { className?: string }) {
  return (
    <div className={cn("size-10 rounded-full bg-primary flex items-center justify-center", className)}>
      <svg viewBox="0 0 24 24" fill="none" className="size-7">
        <motion.path
          d="M7 12.5L10.5 16L17 9"
          stroke="white"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.35, ease: "easeOut", delay: 0.1 }}
        />
      </svg>
    </div>
  )
}

export type PipelineStage = {
  id: string
  label: string
  completed: boolean
  current?: boolean
}

export type DealPipelineProps = {
  stages: PipelineStage[]
  onStageClick?: (stageId: string) => void
  variant?: "display" | "selector"
  className?: string
}

export function DealPipeline({
  stages,
  onStageClick,
  variant = "display",
  className
}: DealPipelineProps) {
  const isSelector = variant === "selector"

  return (
    <div className={cn("flex items-center", className)}>
      {stages.map((stage, index) => {
        const isLast = index === stages.length - 1
        const isClickable = isSelector && onStageClick

        return (
          <div key={stage.id} className="flex items-center flex-1">
            <button
              onClick={() => isClickable && onStageClick(stage.id)}
              disabled={!isClickable}
              className={cn(
                "flex flex-col items-center gap-1.5",
                isClickable && "cursor-pointer",
                !isClickable && "cursor-default"
              )}
            >
              {stage.completed && !stage.current ? (
                <AnimatedCheckCircle />
              ) : (
                <div
                  className={cn(
                    "size-10 rounded-full border-2 transition-all",
                    stage.current && "border-primary bg-primary/20",
                    !stage.current && !stage.completed && "border-muted-foreground/30 bg-muted"
                  )}
                />
              )}
              <span
                className={cn(
                  "text-xs font-medium truncate max-w-[80px]",
                  stage.completed && !stage.current && "text-foreground",
                  stage.current && "text-primary",
                  !stage.completed && !stage.current && "text-muted-foreground"
                )}
              >
                {stage.label}
              </span>
            </button>
            {!isLast && (
              <div
                className={cn(
                  "h-px flex-1 mx-2 self-start mt-4",
                  stage.completed ? "bg-primary" : "bg-border"
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// Simplified version for tighter spaces
export function DealPipelineCompact({
  stages,
  onStageClick,
  variant = "display",
  className
}: DealPipelineProps) {
  const isSelector = variant === "selector"

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {stages.map((stage, index) => {
        const isClickable = isSelector && onStageClick

        return (
          <button
            key={stage.id}
            onClick={() => isClickable && onStageClick(stage.id)}
            disabled={!isClickable}
            className={cn(
              "relative px-3 py-1.5 text-xs font-medium rounded-md transition-all",
              isClickable && "cursor-pointer",
              !isClickable && "cursor-default",

              stage.completed && !stage.current && "bg-primary/20 text-foreground",
              stage.current && "bg-primary text-primary-foreground ring-2 ring-primary/20",
              !stage.completed && !stage.current && "bg-muted text-muted-foreground",

              isClickable && !stage.current && "hover:bg-primary/10"
            )}
          >
            <div className="flex items-center gap-1.5">
              {stage.completed && !stage.current && (
                <AnimatedCheckCircle className="size-3" />
              )}
              <span className="truncate">{stage.label}</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
